import { useState } from 'react';
import Avatar from './Avatar.jsx';
import { MODE_LABEL, placeLabel } from '../lib/format.js';
import { sharedInterests } from '../lib/interests.js';

function distanceKm(a, b) {
  if (![a?.lat, a?.lng, b?.lat, b?.lng].every(Number.isFinite)) return Infinity;
  const radians = (value) => value * Math.PI / 180;
  const dLat = radians(b.lat - a.lat);
  const dLng = radians(b.lng - a.lng);
  const lat1 = radians(a.lat);
  const lat2 = radians(b.lat);
  const h = Math.sin(dLat / 2) ** 2
    + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 6371 * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

function distanceBand(km) {
  if (!Number.isFinite(km)) return 'location unavailable';
  if (km < 10) return 'nearby';
  if (km < 50) return 'within 50 km';
  if (km < 250) return 'within 250 km';
  if (km < 1000) return 'same region';
  return 'elsewhere on the globe';
}

const MODE_FILTERS = [
  [null, 'Everyone'],
  ['offering', 'Offering'],
  ['looking', 'Looking for'],
  ['happening', 'Happening'],
];

export default function PeoplePanel({
  actors, allActors = actors, me, selfActorId, unread, social, onlineActorIds,
  onPick, onRespond, onRespondCompanion, onUnfriend, onEditProfile, onSignIn, onClose,
  initialSource = 'nearby',
}) {
  // Two sources, not a fifth filter: the mode chips say what someone is
  // carrying, this says who they are to you. Different axis, different control.
  const [source, setSource] = useState(initialSource);
  const [mode, setMode] = useState(null);
  const [detent, setDetent] = useState('half');
  const onMap = onlineActorIds ?? new Set(actors.filter((actor) => !actor.away).map((a) => a.id));
  const self = allActors.find((actor) => actor.id === selfActorId);
  const friends = social.friends;
  const requests = social.requestsIn;
  // Reached out to while still browsing anonymously.
  const waiting = social.guestRequests ?? [];
  const friendByActor = new Map(friends.map((friend) => [friend.actorId, friend]));
  const friendPeople = friends.map((friend) => {
    const visible = allActors.find((actor) => actor.id === friend.actorId);
    const merged = { ...friend, ...visible, id: friend.actorId, userId: friend.id };
    if (merged.doing?.expiresAt <= Date.now()) merged.doing = null;
    if (merged.currentUntil <= Date.now()) {
      merged.carrying = false;
      merged.live = false;
      merged.modes = [];
    }
    return {
      friend,
      actor: merged,
      online: onMap.has(friend.actorId),
    };
  }).sort((left, right) => (
    Number(right.online) - Number(left.online)
    || left.friend.displayName.localeCompare(right.friend.displayName)
  ));
  const people = actors
    .filter((actor) => actor.id !== selfActorId)
    // A post with no mode never matches a mode filter: nothing is being asked.
    .filter((actor) => !mode || (actor.modes ?? []).includes(mode))
    .map((actor) => ({
      actor,
      common: sharedInterests(me?.interests, actor.interests),
      distance: distanceKm(self, actor),
    }))
    .sort((left, right) => (
      right.common.length - left.common.length
      || left.distance - right.distance
      || left.actor.displayName.localeCompare(right.actor.displayName)
    ));

  return (
    <aside className={`panel detent-${detent}`} aria-label="People">
      <button
        type="button" className="sheet-handle"
        onClick={() => setDetent((current) => current === 'peek' ? 'half' : current === 'half' ? 'full' : 'peek')}
        aria-label="Resize nearby sheet"
      >
        <span /> {people.length} nearby
      </button>
      <div className="panel-head">
        <div className="panel-sources">
          <button
            type="button"
            className={`chip-btn ${source === 'nearby' ? 'on' : ''}`}
            aria-pressed={source === 'nearby'}
            onClick={() => setSource('nearby')}
          >
            Nearby
          </button>
          <button
            type="button"
            className={`chip-btn ${source === 'friends' ? 'on' : ''}`}
            aria-pressed={source === 'friends'}
            onClick={() => setSource('friends')}
          >
            Friends
          </button>
        </div>
        <button type="button" className="icon-btn" onClick={onClose} aria-label="Close">×</button>
      </div>

      {source === 'nearby' && (
        <div className="panel-filters">
          {MODE_FILTERS.map(([value, label]) => (
            <button
              key={label}
              type="button"
              className={`chip-btn sm ${mode === value ? 'on' : ''}`}
              aria-pressed={mode === value}
              onClick={() => setMode(value)}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      <div className="panel-body">
        {me && (me.interests?.length ?? 0) === 0 && (
          <div className="interest-nudge">
            <b>What are you into?</b>
            <span>Add interests to spot people on the globe who share them.</span>
            <button type="button" className="btn btn-primary btn-sm" onClick={onEditProfile}>
              Add interests
            </button>
          </div>
        )}




        {source === 'friends' && me && friendPeople.length > 0 && (
          <>
            <div className="panel-section">Your people · {friendPeople.length}</div>
            {friendPeople.map(({ friend, actor, online }) => (
              <button
                type="button"
                key={friend.actorId}
                className={`person ${online ? '' : 'away'}`}
                onClick={() => onPick(actor)}
              >
                <span className={`rail-avatar ${actor.carrying ? 'carrying' : ''}`}>
                  <Avatar person={actor} size="sm" online={!actor.away} />
                </span>
                <span className="person-main">
                  <span className="person-name">
                    {actor.displayName}
                    {(actor.modes ?? []).length > 0 && (
                      <span className="mode-tag">{MODE_LABEL[actor.modes[0]]}</span>
                    )}
                  </span>
                  <span className={`person-meta ${actor.doing ? 'doing-line' : ''}`}>
                    {actor.doing
                      ? `${actor.doing.tag}${actor.doing.placeName ? ` · ${actor.doing.placeName}` : ''}`
                      : online ? `here now · ${placeLabel(actor)}` : `away · ${placeLabel(actor)}`}
                  </span>
                </span>
                {unread[friend.actorId] > 0 && <span className="badge">{unread[friend.actorId]}</span>}
                <span
                  className="icon-btn unfriend"
                  role="button"
                  tabIndex={0}
                  title="Remove connection"
                  onClick={(event) => { event.stopPropagation(); onUnfriend(friend.friendshipId); }}
                >
                  ×
                </span>
              </button>
            ))}
          </>
        )}

        {source === 'nearby' && people.length > 0 && (
          <>
            <div className="panel-section">On the globe · {people.length}</div>
            {people.map(({ actor, common, distance }) => {
              const connection = friendByActor.get(actor.id);
              return (
              <button
                type="button"
                key={actor.id}
                className={`person ${common.length ? 'common-person' : ''}`}
                onClick={() => onPick(actor)}
              >
                <span className={`rail-avatar ${actor.carrying ? 'carrying' : ''}`}>
                  <Avatar person={actor} size="sm" online={!actor.away} />
                </span>
                <span className="person-main">
                  <span className="person-name">
                    {actor.displayName}
                    {connection && <span className="star" title="Connection">★</span>}
                    {/* State, never content. A row showing a post body is a
                        listing with a byline attached. */}
                    {(actor.modes ?? []).length > 0 && (
                      <span className="mode-tag">{MODE_LABEL[actor.modes[0]]}</span>
                    )}
                  </span>
                  {actor.doing ? (
                    <span className="person-meta doing-line">
                      {actor.doing.tag}
                      {actor.doing.placeName ? ` · ${actor.doing.placeName}` : ''}
                      {' · '}{distanceBand(distance)}
                    </span>
                  ) : common.length > 0 ? (
                    <span className="person-meta common-meta">
                      {common.slice(0, 2).join(' · ')} · {distanceBand(distance)}
                    </span>
                  ) : (
                    <span className="person-meta">
                      {actor.username ? distanceBand(distance) : 'guest'} · {placeLabel(actor)}
                    </span>
                  )}
                </span>
                {unread[actor.id] > 0 && <span className="badge">{unread[actor.id]}</span>}
                {common.length > 0 && (
                  <span className="common-count" title={`${common.length} shared interests`}>{common.length}</span>
                )}
                {connection && (
                  <span
                    className="icon-btn unfriend"
                    role="button"
                    tabIndex={0}
                    title="Remove connection"
                    onClick={(event) => { event.stopPropagation(); onUnfriend(connection.friendshipId); }}
                  >
                    ×
                  </span>
                )}
              </button>
              );
            })}
          </>
        )}

        {source === 'friends' && me && friends.length === 0 && (
          <p className="empty-note">
            No connections yet. Find someone on the globe and connect through
            something you both care about.
          </p>
        )}

        {source === 'friends' && !me && (
          <p className="empty-note">Sign in to keep a list of people.</p>
        )}

        {source === 'nearby' && people.length === 0 && (
          <p className="empty-note">Nobody else is on the globe right now.</p>
        )}
      </div>
    </aside>
  );
}
